import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AuthImage } from "@/components/AuthImage";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";

export const AssetGallery = ({ a, onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(0);
  const [view, setView] = useState(null);
  const [del, setDel] = useState(null);
  const photos = (a.photos || []).filter((p) => p.gallery);

  const upload = async (files) => {
    const list = [...files];
    if (!list.length) return;
    let last = null;
    for (let i = 0; i < list.length; i++) {
      setBusy(list.length - i);
      const fd = new FormData();
      fd.append("file", list[i]);
      fd.append("gallery", "true");
      try { last = (await api.post(`/assets/${a.id}/photos`, fd)).data; } catch (e) { toast.error(`${list[i].name}: ${errMsg(e)}`); }
    }
    setBusy(0);
    ref.current.value = "";
    if (last) { onDone(last); toast.success(list.length > 1 ? `${list.length} foto caricate` : "Foto caricata"); }
  };
  const act = async (fn, msg) => {
    try { onDone((await fn()).data); toast.success(msg); setView(null); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!photos.length && !a.can.photos) return null;
  return (
    <div className="mt-4" data-testid="asset-gallery">
      <div className="mb-2 flex items-center gap-2">
        <p className="text-sm font-bold">Altre foto <span className="font-normal text-muted-foreground">({photos.length})</span></p>
        {a.can.photos && <>
          <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="asset-gallery-input" />
          <Button variant="outline" size="sm" disabled={busy > 0} onClick={() => ref.current.click()} className="ml-auto rounded-xl" data-testid="asset-gallery-upload">
            <ImagePlus className="mr-1 h-4 w-4" />{busy ? `Caricamento… ${busy}` : "Aggiungi foto"}</Button>
        </>}
      </div>
      {photos.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="asset-gallery-empty">Nessuna foto aggiuntiva.</p> : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          {photos.map((p) => (
            <button key={p.id} type="button" onClick={() => setView(p)} className="relative overflow-hidden rounded-xl border" data-testid={`asset-gallery-photo-${p.id}`}>
              <AuthImage fileId={p.id} alt={p.filename} className="aspect-square w-full" />
              {a.cover_photo_id === p.id && <span className="absolute left-1 top-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">Principale</span>}
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl rounded-2xl" data-testid="asset-gallery-viewer">
          <DialogHeader><DialogTitle className="text-base">{view?.filename} · {view?.by_name} · {fmtDT(view?.at)}</DialogTitle></DialogHeader>
          {view && <AuthImage fileId={view.id} alt={view.filename} className="max-h-[70vh] w-full rounded-xl object-contain" testid="asset-gallery-viewer-image" />}
          {a.can.photos && view && <div className="flex flex-wrap justify-end gap-2">
            {a.cover_photo_id !== view.id && <Button variant="outline" className="rounded-xl" onClick={() => act(() => api.post(`/assets/${a.id}/photos/${view.id}/cover`), "Foto principale aggiornata")} data-testid="asset-gallery-set-cover"><Star className="mr-1 h-4 w-4" />Imposta come principale</Button>}
            <Button variant="outline" className="rounded-xl text-red-600" onClick={() => setDel(view)} data-testid="asset-gallery-delete"><Trash2 className="mr-1 h-4 w-4" />Elimina</Button>
          </div>}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] rounded-2xl" data-testid="asset-gallery-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Eliminare questa foto?</AlertDialogTitle>
            <AlertDialogDescription>La foto non sarà più visibile nella scheda. Nello storico del mezzo resta traccia dell'eliminazione.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-red-600 hover:bg-red-700" onClick={() => { const p = del; setDel(null); act(() => api.delete(`/assets/${a.id}/photos/${p.id}`), "Foto eliminata"); }} data-testid="asset-gallery-delete-confirm">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
